import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MotionContainer, varBounceIn } from 'src/components/animate';
import { PATH_DASHBOARD } from 'src/routes/paths';
import {
  DONATION_ACTION,
  DONATION_ERROR,
  formatOccurrenceDate,
  isRetryable,
} from 'src/utils/donationAvailability';

/**
 * Shown when a donation detail page has nothing to render.
 *
 * The point of this screen is to stop saying "this donation no longer exists"
 * for every failure. A request that never completed is retryable and the
 * donation may be perfectly fine. When the server names a reason (claimed,
 * removed, cancelled, wrong organization, ...) we say that and offer the
 * matching next step; otherwise a 403 truthfully means unavailable *to this
 * organization*.
 */
export default function DonationUnavailable({
  kind,
  reason,
  donationId,
  onRetry,
  organizationName,
  otherOrganizations = [],
  onSwitchOrganization,
}) {
  const { t } = useTranslation();
  const switchButtonRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const retryable = isRetryable(kind);
  const namedReason = kind === DONATION_ERROR.UNAVAILABLE ? reason : null;
  // "Not available to {{org}}" needs the org's name; without it the generic
  // copy says the same thing.
  const reasonMessage =
    namedReason &&
    (namedReason.code !== 'WRONG_ORGANIZATION' || organizationName)
      ? t(namedReason.messageKey, { org: organizationName })
      : null;
  const occurrenceDate = reasonMessage
    ? formatOccurrenceDate(namedReason.occurrence)
    : null;

  let title;
  let description;
  if (retryable) {
    title = t('donations.unavailable.load-failed-title');
    description = t('donations.unavailable.load-failed-description');
  } else if (kind === DONATION_ERROR.NOT_FOUND) {
    title = t('donations.unavailable.not-found-title');
    description = t('donations.unavailable.not-found-description');
  } else if (reasonMessage) {
    title = reasonMessage;
    description = occurrenceDate
      ? t('donations.unavailable.occurrence', { date: occurrenceDate })
      : null;
  } else {
    title = t('donations.unavailable.unavailable-title');
    description = organizationName
      ? t('donations.unavailable.unavailable-description-org', {
          org: organizationName,
        })
      : t('donations.unavailable.unavailable-description');
  }

  // Only an unavailable donation might resolve under another organization; a
  // failed request or a bad link would fail there too.
  const canSwitch =
    otherOrganizations.length > 0 &&
    kind === DONATION_ERROR.UNAVAILABLE &&
    typeof onSwitchOrganization === 'function';
  const rescuePath =
    donationId && namedReason?.rescueId
      ? PATH_DASHBOARD.viewPartialDonation(donationId, namedReason.rescueId)
      : null;

  let primaryAction = DONATION_ACTION.FIND_DONATIONS;
  if (retryable) {
    primaryAction = DONATION_ACTION.RETRY;
  } else if (reasonMessage) {
    primaryAction = namedReason.action;
  }
  // Fall back to finding other food when the named action can't be offered.
  if (
    (primaryAction === DONATION_ACTION.SWITCH_ORGANIZATION && !canSwitch) ||
    ((primaryAction === DONATION_ACTION.VIEW_RESCUE ||
      primaryAction === DONATION_ACTION.VIEW_RESCUE_RECORD) &&
      !rescuePath)
  ) {
    primaryAction = DONATION_ACTION.FIND_DONATIONS;
  }

  let secondaryAction = null;
  if (primaryAction === DONATION_ACTION.SWITCH_ORGANIZATION) {
    secondaryAction = DONATION_ACTION.FIND_DONATIONS;
  } else if (primaryAction === DONATION_ACTION.FIND_DONATIONS && canSwitch) {
    secondaryAction = DONATION_ACTION.SWITCH_ORGANIZATION;
  }

  const handleSwitchClick = () => {
    if (otherOrganizations.length === 1) {
      onSwitchOrganization(otherOrganizations[0].team_id);
      return;
    }
    setMenuOpen(true);
  };

  const renderAction = (action, variant) => {
    const linkProps = { size: 'large', variant, component: RouterLink };
    switch (action) {
      case DONATION_ACTION.RETRY:
        return (
          <Button size="large" variant={variant} onClick={onRetry}>
            {t('donations.unavailable.try-again')}
          </Button>
        );
      case DONATION_ACTION.SWITCH_ORGANIZATION:
        return (
          <>
            <Button
              ref={switchButtonRef}
              size="large"
              variant={variant}
              onClick={handleSwitchClick}
            >
              {t('donations.unavailable.switch-organization')}
            </Button>
            <Menu
              anchorEl={switchButtonRef.current}
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
            >
              {otherOrganizations.map((org) => (
                <MenuItem
                  key={org.team_id}
                  onClick={() => {
                    setMenuOpen(false);
                    onSwitchOrganization(org.team_id);
                  }}
                >
                  {org.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      case DONATION_ACTION.VIEW_RESCUE:
        return (
          <Button {...linkProps} to={rescuePath}>
            {t('donations.unavailable.view-your-rescue')}
          </Button>
        );
      case DONATION_ACTION.VIEW_RESCUE_RECORD:
        return (
          <Button {...linkProps} to={rescuePath}>
            {t('donations.unavailable.view-rescue-record')}
          </Button>
        );
      case DONATION_ACTION.VIEW_ASSIGNMENTS:
        return (
          <Button {...linkProps} to={PATH_DASHBOARD.myrescues}>
            {t('donations.unavailable.view-my-assignments')}
          </Button>
        );
      default:
        return (
          <Button {...linkProps} to={PATH_DASHBOARD.findItems.donations}>
            {t('donations.unavailable.find-other-donations')}
          </Button>
        );
    }
  };

  return (
    <Container>
      <MotionContainer initial="initial" open>
        <Box sx={{ maxWidth: 480, margin: 'auto', textAlign: 'center', mt: 4 }}>
          <motion.div variants={varBounceIn}>
            <Typography
              variant={reasonMessage ? 'h4' : 'h3'}
              gutterBottom
              role="status"
              tabIndex={-1}
            >
              {title}
            </Typography>
          </motion.div>
          {description ? (
            <Typography sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          ) : null}

          <Box
            component={motion.img}
            variants={varBounceIn}
            alt=""
            src="/static/illustrations/illustration_404.svg"
            sx={{ width: '100%', maxHeight: 240, my: { xs: 5, sm: 10 } }}
          />

          <Stack
            spacing={1.5}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="center"
          >
            {renderAction(primaryAction, 'contained')}
            {secondaryAction ? renderAction(secondaryAction, 'outlined') : null}
          </Stack>
        </Box>
      </MotionContainer>
    </Container>
  );
}

DonationUnavailable.propTypes = {
  kind: PropTypes.string,
  /** From getDonationUnavailableReason; null for the generic copy. */
  reason: PropTypes.shape({
    code: PropTypes.string,
    messageKey: PropTypes.string,
    action: PropTypes.string,
    occurrence: PropTypes.object,
    rescueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  donationId: PropTypes.string,
  onRetry: PropTypes.func,
  organizationName: PropTypes.string,
  otherOrganizations: PropTypes.arrayOf(
    PropTypes.shape({
      team_id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  onSwitchOrganization: PropTypes.func,
};
